import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { queryClient } from '@/app/queryClient'
import { friendlyAuthError, loadProfile, signOut as signOutRequest, watchSession } from '@/data/auth'
import type { Profile } from './profile'

/**
 * What the rest of the app knows about the visitor.
 *  loading     we have not yet heard from the server
 *  signed-out  nobody is signed in
 *  signed-in   someone is; `profile` is null if their login has no church record
 *  error       we could not load the profile (almost always a bad connection)
 */
export type AuthState =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'signed-in'; userId: string; profile: Profile | null }
  | { status: 'error'; message: string }

interface AuthApi {
  auth: AuthState
  /** Re-read the profile (for example after the first-login password change). */
  refresh: () => Promise<void>
  signOut: () => Promise<void>
}

const Ctx = createContext<AuthApi | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  // undefined = not heard yet, null = signed out, string = signed-in user id.
  const [userId, setUserId] = useState<string | null | undefined>(undefined)
  const [profile, setProfile] = useState<Profile | null | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)
  const [reloadTick, setReloadTick] = useState(0)

  useEffect(() => watchSession(setUserId), [])

  // A different person (or nobody) means the old profile is stale: drop it at
  // once so it can never be shown for even a moment. A plain refresh (same
  // person, reloadTick) deliberately does NOT do this, so there is no flash.
  useEffect(() => {
    setProfile(undefined)
  }, [userId])

  useEffect(() => {
    if (userId === undefined) return
    if (userId === null) {
      setProfile(undefined)
      setError(null)
      // Never let one person's cached data outlive their session: the next user
      // of a shared phone must not glimpse it.
      queryClient.clear()
      return
    }
    let cancelled = false
    setError(null)
    loadProfile(userId).then(
      (p) => {
        if (!cancelled) setProfile(p)
      },
      (e) => {
        if (!cancelled) setError(friendlyAuthError(e))
      },
    )
    return () => {
      cancelled = true
    }
  }, [userId, reloadTick])

  const refresh = useCallback(async () => {
    setReloadTick((n) => n + 1)
  }, [])

  const signOut = useCallback(async () => {
    await signOutRequest()
    queryClient.clear()
  }, [])

  const auth = useMemo<AuthState>(() => {
    if (userId === undefined) return { status: 'loading' }
    if (userId === null) return { status: 'signed-out' }
    if (error) return { status: 'error', message: error }
    if (profile === undefined) return { status: 'loading' }
    return { status: 'signed-in', userId, profile }
  }, [userId, profile, error])

  const value = useMemo(() => ({ auth, refresh, signOut }), [auth, refresh, signOut])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth(): AuthApi {
  const v = useContext(Ctx)
  if (!v) throw new Error('useAuth must be used inside <SessionProvider>')
  return v
}

/** The signed-in profile; throws if used on a screen that is not behind sign-in. */
export function useProfile(): Profile {
  const { auth } = useAuth()
  if (auth.status !== 'signed-in' || !auth.profile) {
    throw new Error('useProfile used while nobody is signed in')
  }
  return auth.profile
}
